import { Link, useNavigate, useParams } from "react-router";
import { BookAppointment } from "@/design/BookAppointment";
import { BusinessLogo } from "@/design/BusinessLogo";
import { googleMapsHref } from "@/design/maps";
import { formStateFor } from "@/modules/booking/commands/consumerCopy";
import { useMe } from "~/auth/MeProvider";
import { post } from "~/lib/api";
import { ClinicFrame, useClinic } from "./clinicProfile";

/**
 * ClinicBookingScreen (docs/ux/screens/ClinicBookingScreen.md), the clinic's
 * booking view, opened from the details view's **Book an appointment** (H59).
 *
 * The booking section moved here whole: the one service list, a card per
 * Service Location with its own total (H24), the day-first times and one Send
 * request. **One booking surface per clinic (H45)**: no practitioner list or
 * chooser; the clinic assigns somebody at approval.
 */
export function ClinicBookingScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { me } = useMe();
  const clinic = useClinic(id);

  // Past the route guard the caller is a Consumer or a guest, but the widget
  // still distinguishes all three: "other" shows availability without a form
  // if the guard is ever loosened.
  const viewer: "consumer" | "guest" | "other" =
    me?.kind === "consumer" ? "consumer" : !me || me.kind === "guest" ? "guest" : "other";

  return (
    <ClinicFrame clinic={clinic}>
      {(data) => {
        const { business } = data;
        const details = `/businesses/${business.id}`;
        const here = `${details}/book`;

        return (
          <>
            <Link to={details} className="mb-4 inline-block text-sm font-medium text-accent">
              ‹ Back to clinic
            </Link>

            <div className="mb-6 flex items-center gap-3">
              <BusinessLogo logoUrl={business.logoUrl} name={business.name} size={40} />
              <p className="min-w-0 truncate text-lg font-bold text-ink">{business.name}</p>
            </div>

            <BookAppointment
              businessId={business.id}
              services={data.bookServices}
              days={data.availability}
              daysByLocation={data.availabilityByLocation}
              viewer={viewer}
              offeredLocations={data.offeredLocations}
              mapHref={googleMapsHref(business.latitude, business.longitude)}
              loginHref={`/login?next=${encodeURIComponent(here)}`}
              onNavigate={(href) => void navigate(href)}
              onRequest={async (input) => {
                const result = await post<{ bookingId: string }>("/bookings", input);
                // Booking's own copy map, so "that time was just taken" reads
                // the same as it always has.
                return result.ok ? { success: "requested" } : formStateFor(result.failure);
              }}
            />
          </>
        );
      }}
    </ClinicFrame>
  );
}
